import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

const SCALER_URL = 'https://bro-scaller.av-mediaserver.app:8080';

// Singleton class to manage voice-related functionality globally
class VoiceService {
  private socket: WebSocket | null = null;
  private audioCtx: AudioContext | null = null;
  private recorder: ScriptProcessorNode | null = null;
  private stream: MediaStream | null = null;
  private socketUuid = uuidv4();
  private textUpdateCallback: ((text: string, isTranslated?: boolean) => void) | null = null;
  private recordingStatusCallback: ((isRecording: boolean) => void) | null = null;
  private subtitlesCallback: ((subtitles: string[]) => void) | null = null;
  private language = 'en';
  private inputLanguage = 'en';
  private isTranscribe = false;
  public cancelRequested = false;

  // Configure the service with callbacks and options
  configure({
    language = 'en',
    inputLanguage = 'en',
    setTextAreaValue,
    setIsRecordingInProcess,
    isTranscribe = false,
    setRunOnValue,
  }: {
    language?: string;
    inputLanguage?: string;
    setTextAreaValue?: (value: string, isTranslated?: boolean) => void;
    setIsRecordingInProcess?: (isRecording: boolean) => void;
    isTranscribe?: boolean;
    setRunOnValue?: (subtitles: string[]) => void;
  }) {
    this.language = language;
    this.inputLanguage = inputLanguage;
    this.isTranscribe = isTranscribe;
    this.cancelRequested = false;

    if (setTextAreaValue) this.textUpdateCallback = setTextAreaValue;
    if (setIsRecordingInProcess) this.recordingStatusCallback = setIsRecordingInProcess;
    if (setRunOnValue) this.subtitlesCallback = setRunOnValue;
  }

  private resampleTo16kHZ = (audioData: Float32Array, origSampleRate = 44100): Float32Array => {
    const targetLength = Math.round(audioData.length * (16000 / origSampleRate));
    const resampledData = new Float32Array(targetLength);
    const springFactor = (audioData.length - 1) / (targetLength - 1);
    resampledData[0] = audioData[0];
    resampledData[targetLength - 1] = audioData[audioData.length - 1];

    for (let i = 1; i < targetLength - 1; i++) {
      const index = i * springFactor;
      const leftIndex = Math.floor(index);
      const rightIndex = Math.ceil(index);
      const fraction = index - leftIndex;
      resampledData[i] = audioData[leftIndex] + (audioData[rightIndex] - audioData[leftIndex]) * fraction;
    }

    return resampledData;
  };

  private float32ArrayToBase64 = (float32Array: Float32Array): string => {
    const uint8Array = new Uint8Array(float32Array.buffer);
    let binaryString = '';
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i]);
    }
    return btoa(binaryString);
  };

  private getRecorder = (stream: MediaStream) => {
    const audioCtx = new AudioContext();
    this.audioCtx = audioCtx;

    const mediaStream = audioCtx.createMediaStreamSource(stream);
    const recorder = audioCtx.createScriptProcessor(8192, 1, 1);

    recorder.onaudioprocess = (event) => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        const inputData = event.inputBuffer.getChannelData(0);
        const audioData16kHz = this.resampleTo16kHZ(inputData, audioCtx.sampleRate);
        const packet = {
          allLangs: [this.language],
          audio: this.float32ArrayToBase64(audioData16kHz),
          isStartStream: true,
          returnTranslatedSegments: true,
          disableSentenceCutter: this.language === this.inputLanguage,
          speakerLang: this.inputLanguage,
        };
        this.socket.send(JSON.stringify(packet));
      }
    };

    mediaStream.connect(recorder);
    recorder.connect(audioCtx.destination);
    this.recorder = recorder;
  };

  // Connect to the Whisper API
  async connectToWhisper() {

    if (this.cancelRequested) return;

    this.cancelRequested = false;

    try {
      const result = await axios.get(`${SCALER_URL}/api/v1/scaler/find-free-machine/avmed`);

      if (!result?.data) return false;

      this.socket = new WebSocket(`wss://${result.data.dns}:${result.data.port}`);

      this.socket.onopen = () => {
        this.socket?.send(JSON.stringify({
          uid: this.socketUuid,
          task: 'transcribe',
          model: 'large-v3',
          use_vad: true,
        }));
      };

      // Track the last processed message to avoid duplicates
      let lastOriginalText = '';
      // Track translations by language code
      const translationsByLanguage = {};

      this.socket.onmessage = (event: MessageEvent) => {
        const data = JSON.parse(event.data);
        if (!data) return;

        // Handle direct transcription (same input and output language)
        if (this.language === this.inputLanguage && this.isTranscribe) {
          if (!Array.isArray(data?.segments)) return;
          const resultText = data.segments.map((item: { text: string }) => item?.text?.trim()).join(' ');
          
          // Only update if the text has changed
          if (resultText !== lastOriginalText) {
            lastOriginalText = resultText;
            this.textUpdateCallback?.(resultText);
          }
          return;
        }

        // Handle translation from one language to another
        if (this.language !== this.inputLanguage) {
          // Check if we have translated segments in the target language
          if (data?.segments?.[this.language]) {
            // This is the translated text in the target language
            const translatedText = data.segments[this.language];
            
            // Skip if the translated text is empty
            if (!translatedText || translatedText.trim() === '') return;
            
            // Initialize if this is the first translation for this language
            if (!translationsByLanguage[this.language]) {
              translationsByLanguage[this.language] = '';
            }
            
            // Check if this is new content to append (avoid duplicates)
            // Use a more strict check to ensure we don't append the same content
            if (!translationsByLanguage[this.language].endsWith(translatedText) && 
                !translationsByLanguage[this.language].includes(translatedText)) {
              
              // If we already have content, add a space before appending
              const separator = translationsByLanguage[this.language] ? ' ' : '';
              
              // Append ONLY the new translation to our stored value
              const updatedTranslation = translationsByLanguage[this.language] + 
                (translationsByLanguage[this.language] ? separator : '') + 
                translatedText;
              
              // Update our stored translation
              translationsByLanguage[this.language] = updatedTranslation;
              
              // Send ONLY the target language translation to the callback
              this.textUpdateCallback?.(updatedTranslation, true);
            }
          } else if (data?.segments) {
            // If there's no translated segment but we have transcribed segments
            // For English to Hindi translation, we should NOT send the original English text
            // Only process this for cases where we're not expecting a translation
            if (Array.isArray(data.segments) && 
                !(this.inputLanguage === 'en' && this.language === 'hi')) {
              const resultText = data.segments.map((item: { text: string }) => item?.text?.trim()).join(' ');
              
              // Only update if the text has changed
              if (resultText !== lastOriginalText) {
                lastOriginalText = resultText;
                // Only send this if we're not in English->Hindi translation mode
                this.textUpdateCallback?.(resultText, false);
              }
            }
            // For English->Hindi, we simply wait for the next message with a proper translation
          }
        } else {
          // Handle regular transcription with subtitle updates
          const { segments } = data;
          if (segments) {
            const resultText = segments.map((item: { text: string }) => item?.text?.trim()).join(' ');
            
            // Only update if the text has changed
            if (resultText !== lastOriginalText) {
              lastOriginalText = resultText;
              this.textUpdateCallback?.(resultText);
            }
            
            // Update subtitles separately
            const subtitles = segments?.slice(-3)?.map((segment: { text: string }) => segment?.text);
            this.subtitlesCallback?.(subtitles);
          }
        }
      };

      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  // Start streaming audio to the service
  async startStreaming() {
    this.recordingStatusCallback?.(true);
    if (!this.audioCtx) this.audioCtx = new AudioContext();
    if (this.audioCtx.state === 'suspended') await this.audioCtx.resume();

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: false,
          echoCancellation: false,
        },
      });
      this.stream = stream;
      this.getRecorder(stream);
    } catch (e) {
      console.error('[startStreaming] media stream request failed:', e);
    }
  }

  // Pause audio streaming
  async pauseStreaming() {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
      if (this.audioCtx) {
        this.audioCtx.close();
        this.audioCtx = null;
      }
    } else {
      await this.startStreaming();
    }
  }

  // Stop streaming and clean up resources
  stopStreaming() {
    this.cancelRequested = true;

    this.socket?.send('END_OF_AUDIO');
    this.recordingStatusCallback?.(false);

    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;

    this.recorder?.disconnect();
    this.recorder = null;

    this.audioCtx?.close();
    this.audioCtx = null;

    this.socket?.close();
    this.socket = null;
  }

  // Delete streaming resources without sending END_OF_AUDIO
  deleteStreaming() {
    this.cancelRequested = true;

    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send("END_OF_AUDIO");
    }

    this.socket?.close();
    this.socket = null;

    this.audioCtx?.close();
    this.audioCtx = null;

    this.recorder?.disconnect();
    this.recorder = null;

    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
  }

  // Get the current WebSocket state
  getSocketState() {
    return this.socket?.readyState || WebSocket.CLOSED;
  }
}

// Create and export a singleton instance
export const voiceService = new VoiceService();

// Expose the service to the global window object
if (typeof window !== 'undefined') {
  // Add global voice service to window
  (window as any).voiceService = voiceService;
}

// Also export as default for flexibility
export default voiceService;
